// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\utils\authConfig.js
//
// Email-only launch switch for the screens (pre-launch M1, 13 Oct 2026).
// Reads GET /api/public/auth-config once per page load:
//   mobileOtpRequired  true  → the mobile number is verified with an SMS
//                              code (registration, password reset, turned-18
//                              move), as before.
//                      false → email-only: the mobile number is asked for
//                              but not verified; codes and reset links go
//                              by email.
//   whatsappEnabled    false → WhatsApp messages are switched off.
// Until the answer arrives (or if it can't be read) the screens behave as
// before (mobile code required) — the server decides in the end anyway.
// Server side: config/authOptions.js (REQUIRE_MOBILE_OTP in .env).
// ============================================================

import { useEffect, useState } from 'react';
import { publicAPI } from '../api';

const DEFAULTS = { mobileOtpRequired: true, whatsappEnabled: true, loaded: false };
let cached = null;
let pending = null;
const listeners = new Set();

export function loadAuthConfig() {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = publicAPI.getAuthConfig()
      .then(r => {
        cached = {
          // only an explicit false switches a feature off
          mobileOtpRequired: !(r && r.mobileOtpRequired === false),
          whatsappEnabled: !(r && r.whatsappEnabled === false),
          loaded: true,
        };
        listeners.forEach(fn => fn(cached));
        return cached;
      })
      .catch(() => { pending = null; return DEFAULTS; });
  }
  return pending;
}

// Ask the server again (e.g. it refused a mobile code with MOBILE_OTP_OFF
// because it is in email-only mode) — every screen using the hook updates.
export function reloadAuthConfig() {
  cached = null; pending = null;
  return loadAuthConfig();
}

export function useAuthConfig() {
  const [cfg, setCfg] = useState(cached || DEFAULTS);
  useEffect(() => {
    let alive = true;
    const onChange = (c) => { if (alive) setCfg(c); };
    listeners.add(onChange);
    loadAuthConfig().then(onChange);
    return () => { alive = false; listeners.delete(onChange); };
  }, []);
  return cfg;
}
